// BaseLimitErrors.js

export const BASE_LIMIT_CODES = Object.freeze({
  AI_DISABLED: 'ai_disabled_for_plan',
  FILE_SIZE: 'file_size_limit_exceeded',
  MEMBER: 'member_limit_reached',
  STORAGE: 'base_limit_reached',
  USAGE_ALLOWANCE: 'base_usage_allowance_exhausted',
});

const LEGACY_STORAGE_CODE = 'base_limit_reached';
const LEGACY_USAGE_ALLOWANCE_CODE = 'base_usage_allowance_exhausted';

export class BaseLimitError extends Error {
  constructor(message, details = {}) {
    super(message);
    this.name = 'BaseLimitError';
    this.code = String(details.code || BASE_LIMIT_CODES.STORAGE).trim();
    this.limitType = String(details.limitType || '').trim() || null;
    this.limit = Number.isFinite(Number(details.limit)) ? Number(details.limit) : null;
    this.used = Number.isFinite(Number(details.used)) ? Number(details.used) : null;
    this.unit = String(details.unit || '').trim() || null;
  }
}

function parseDetailsObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return value;
}

function parseFirebaseCallableDetails(error) {
  const candidates = [
    parseDetailsObject(error?.details),
    parseDetailsObject(error?.customData),
    parseDetailsObject(error?.customData?.details),
  ];

  for (const details of candidates) {
    if (details.code || details.limitType) {
      return details;
    }
  }

  return {};
}

export function parseBaseLimitError(error) {
  if (error instanceof BaseLimitError) {
    return error;
  }

  const details = {
    ...parseDetailsObject(error?.customData),
    ...parseDetailsObject(error?.details),
    ...parseFirebaseCallableDetails(error),
  };
  const message = String(error?.message || '').trim();

  if (details.code || details.limitType) {
    const normalizedDetails = { ...details };
    if (String(normalizedDetails.code || '').trim() === LEGACY_STORAGE_CODE) {
      normalizedDetails.code = BASE_LIMIT_CODES.STORAGE;
    }
    return new BaseLimitError(message || 'Base limit reached.', normalizedDetails);
  }

  if (/out of storage/i.test(message)) {
    return new BaseLimitError(message, {
      code: BASE_LIMIT_CODES.STORAGE,
      limitType: 'storage',
      unit: 'bytes',
      ...details,
    });
  }

  if (/upload limit/i.test(message)) {
    return new BaseLimitError(message, {
      code: BASE_LIMIT_CODES.FILE_SIZE,
      limitType: 'file_size',
      unit: 'bytes',
      ...details,
    });
  }

  if (/member limit/i.test(message)) {
    return new BaseLimitError(message, {
      code: BASE_LIMIT_CODES.MEMBER,
      limitType: 'members',
      ...details,
    });
  }

  const firebaseCode = String(error?.code || '').trim();
  const detailCode = String(details.code || '').trim();
  if (
    detailCode === BASE_LIMIT_CODES.USAGE_ALLOWANCE
    || detailCode === LEGACY_USAGE_ALLOWANCE_CODE
    || details.legacyCode === LEGACY_USAGE_ALLOWANCE_CODE
    || (
      (firebaseCode === 'functions/resource-exhausted' || firebaseCode === 'resource-exhausted')
      && (details.limitType === 'usage_allowance' || details.limitType === 'teamUsage')
    )
    || /included usage|usage allowance|out of credits/i.test(message)
  ) {
    return new BaseLimitError(message || 'Space usage limit reached.', {
      ...details,
      code: BASE_LIMIT_CODES.USAGE_ALLOWANCE,
      limitType: details.limitType || 'usage_allowance',
      unit: details.unit || 'credits',
    });
  }

  if (
    detailCode === BASE_LIMIT_CODES.AI_DISABLED
    || detailCode === 'ai_disabled_for_plan'
    || /Ai is not included/i.test(message)
  ) {
    return new BaseLimitError(message || 'Ai is not included on this plan.', {
      ...details,
      code: BASE_LIMIT_CODES.AI_DISABLED,
      limitType: details.limitType || 'ai_disabled',
    });
  }

  return null;
}

export function isBaseLimitError(error) {
  return parseBaseLimitError(error) != null;
}
